'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navbar
    'nav.services': 'Services',
    'nav.process': 'Process',
    'nav.portfolio': 'Portfolio',
    'nav.about': 'About Us',
    'nav.start': 'Start Project',

    // Hero
    'hero.badge': 'Enterprise Excellence',
    'hero.title.1': 'High-Performance',
    'hero.title.2': 'Corporate Websites.',
    'hero.description': "We develop robust platforms using the market's best tech stacks, hosted on the world's fastest servers to ensure maximum stability and speed.",
    'hero.cta.proposal': 'Get a Proposal',
    'hero.cta.work': 'View Our Work',
    'hero.stat.speed': 'Global Edge Servers',
    'hero.stat.roi': 'Modern Stacks',
    'hero.stat.global': 'Enterprise Grade',
    'hero.card.seo': 'Performance Score',
    'hero.card.ads': 'Cutting-Edge Tech Stack',

    // Stats
    'stats.retention': 'Client Retention',
    'stats.projects': 'Projects Delivered',
    'stats.roi': 'Average ROI',
    'stats.support': 'Support & Monitoring',

    // Services
    'services.title.1': 'Engineered for',
    'services.title.2': 'Growth',
    'services.description': "We don't just build websites; we build digital ecosystems designed to outperform your competition.",
    'services.corp.title': 'Corporate Websites',
    'services.corp.desc': 'Bespoke digital experiences designed to convert. Fast, secure, and scalable architectures built on Next.js.',
    'services.seo.title': 'Advanced SEO',
    'services.seo.desc': 'Dominate search results with our technical SEO audits, content strategy, and authority building.',
    'services.ads.title': 'Google Ads Management',
    'services.ads.desc': 'High-ROI campaigns managed by certified experts. We optimize every click to maximize your revenue.',
    'services.apps.title': 'Custom Web Apps',
    'services.apps.desc': 'Complex functionality made simple. We build internal tools, portals, and SaaS platforms.',
    'services.mobile.title': 'Mobile-First Design',
    'services.mobile.desc': 'Flawless experiences across all devices. Responsive design is at the core of everything we build.',
    'services.security.title': 'Security & Maintenance',
    'services.security.desc': 'Enterprise-grade security protocols and 24/7 monitoring to keep your digital assets safe.',

    // Portfolio
    'portfolio.title.1': 'Our Recent',
    'portfolio.title.2': 'Projects',
    'portfolio.description': 'Explore some of our recent work and discover how we help businesses achieve digital dominance.',
    'portfolio.view_project': 'View Project',

    // About
    'about.title.1': 'Who',
    'about.title.2': 'We Are',
    'about.description.1': 'Webzuk is not just another agency; we are a strategic technology partner focused on delivering results.',
    'about.description.2': 'We combine robust development, cutting-edge design, and advanced tactics to deliver solutions that drive conversion.',
    'about.stat.1.value': '10+',
    'about.stat.1.label': 'Years Experience',
    'about.stat.2.value': '50+',
    'about.stat.2.label': 'Enterprises Served',

    // Process
    'process.title.1': 'Our Proven',
    'process.title.2': 'Methodology',
    'process.description': "We don't rely on guesswork. Our process is data-driven, transparent, and focused on delivering measurable results for your business.",
    'process.list.1': 'Agile Development Cycles',
    'process.list.2': 'Weekly Progress Reports',
    'process.list.3': 'Dedicated Project Manager',
    'process.list.4': 'Post-Launch Support',
    'process.step.1.title': 'Discovery & Strategy',
    'process.step.1.desc': 'We dive deep into your business goals, audience, and competitors to craft a winning roadmap.',
    'process.step.2.title': 'Design & UX',
    'process.step.2.desc': 'Our designers create visually stunning, user-centric interfaces that align with your brand identity.',
    'process.step.3.title': 'Development',
    'process.step.3.desc': 'We code with precision using modern frameworks like Next.js for speed, security, and scalability.',
    'process.step.4.title': 'Launch & Growth',
    'process.step.4.desc': 'After a flawless launch, we optimize continuously with SEO and Ads to drive traffic and conversions.',

    // CTA
    'cta.title': 'Ready to dominate your market?',
    'cta.description': "Let's discuss how we can elevate your digital presence and drive real business growth.",
    'cta.form.name': 'Name',
    'cta.form.company': 'Company',
    'cta.form.email': 'Email',
    'cta.form.message': 'Message',
    'cta.form.send': 'Send Message',

    // Footer
    'footer.desc': 'We help ambitious brands scale through high-performance web development and data-driven digital marketing.',
    'footer.services': 'Services',
    'footer.company': 'Company',
    'footer.rights': '© 2024 Webzuk. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.links.web': 'Web Development',
    'footer.links.seo': 'SEO Optimization',
    'footer.links.ads': 'Google Ads',
    'footer.links.ui': 'UI/UX Design',
    'footer.links.about': 'About Us',
    'footer.links.case': 'Case Studies',
    'footer.links.careers': 'Careers',
    'footer.links.contact': 'Contact',
  },
  pt: {
    // Navbar
    'nav.services': 'Serviços',
    'nav.process': 'Processo',
    'nav.portfolio': 'Portfólio',
    'nav.about': 'Sobre Nós',
    'nav.start': 'Iniciar Projeto',

    // Hero
    'hero.badge': 'Excelência Corporativa',
    'hero.title.1': 'Sites Corporativos de',
    'hero.title.2': 'Alta Performance.',
    'hero.description': 'Desenvolvemos plataformas robustas utilizando as melhores stacks do mercado, hospedadas nos servidores mais rápidos do mundo para garantir máxima velocidade e estabilidade.',
    'hero.cta.proposal': 'Solicitar Proposta',
    'hero.cta.work': 'Ver Nosso Trabalho',
    'hero.stat.speed': 'Servidores Globais',
    'hero.stat.roi': 'Stacks Modernas',
    'hero.stat.global': 'Padrão Enterprise',
    'hero.card.seo': 'Score de Performance',
    'hero.card.ads': 'Tecnologia de Ponta',

    // Stats
    'stats.retention': 'Retenção de Clientes',
    'stats.projects': 'Projetos Entregues',
    'stats.roi': 'ROI Médio',
    'stats.support': 'Suporte e Monitoramento',

    // Services
    'services.title.1': 'Projetado para o',
    'services.title.2': 'Crescimento',
    'services.description': 'Não construímos apenas sites; construímos ecossistemas digitais projetados para superar sua concorrência.',
    'services.corp.title': 'Sites Corporativos',
    'services.corp.desc': 'Experiências digitais sob medida projetadas para converter. Arquiteturas rápidas, seguras e escaláveis construídas em Next.js.',
    'services.seo.title': 'SEO Avançado',
    'services.seo.desc': 'Domine os resultados de busca com nossas auditorias técnicas de SEO, estratégia de conteúdo e construção de autoridade.',
    'services.ads.title': 'Gestão de Google Ads',
    'services.ads.desc': 'Campanhas de alto ROI gerenciadas por especialistas certificados. Otimizamos cada clique para maximizar sua receita.',
    'services.apps.title': 'Web Apps Personalizados',
    'services.apps.desc': 'Funcionalidade complexa simplificada. Construímos ferramentas internas, portais e plataformas SaaS.',
    'services.mobile.title': 'Design Mobile-First',
    'services.mobile.desc': 'Experiências impecáveis em todos os dispositivos. O design responsivo está no centro de tudo o que construímos.',
    'services.security.title': 'Segurança e Manutenção',
    'services.security.desc': 'Protocolos de segurança de nível empresarial e monitoramento 24/7 para manter seus ativos digitais seguros.',

    // Portfolio
    'portfolio.title.1': 'Nossos Projetos',
    'portfolio.title.2': 'Recentes',
    'portfolio.description': 'Explore alguns dos nossos trabalhos recentes e descubra como ajudamos empresas a alcançar o domínio digital.',
    'portfolio.view_project': 'Ver Projeto',

    // About
    'about.title.1': 'Quem',
    'about.title.2': 'Somos',
    'about.description.1': 'A Webzuk não é apenas mais uma agência, somos um parceiro estratégico de tecnologia focado em entregar resultados.',
    'about.description.2': 'Unimos desenvolvimento robusto, design de ponta e táticas avançadas para entregar soluções que gerem conversão.',
    'about.stat.1.value': '+10',
    'about.stat.1.label': 'Anos de Experiência',
    'about.stat.2.value': '50+',
    'about.stat.2.label': 'Empresas Atendidas',

    // Process
    'process.title.1': 'Nossa Metodologia',
    'process.title.2': 'Comprovada',
    'process.description': 'Não dependemos de suposições. Nosso processo é baseado em dados, transparente e focado em entregar resultados mensuráveis para o seu negócio.',
    'process.list.1': 'Ciclos de Desenvolvimento Ágil',
    'process.list.2': 'Relatórios de Progresso Semanais',
    'process.list.3': 'Gerente de Projeto Dedicado',
    'process.list.4': 'Suporte Pós-Lançamento',
    'process.step.1.title': 'Descoberta e Estratégia',
    'process.step.1.desc': 'Mergulhamos fundo em seus objetivos de negócios, público e concorrentes para criar um roteiro vencedor.',
    'process.step.2.title': 'Design e UX',
    'process.step.2.desc': 'Nossos designers criam interfaces visualmente impressionantes e centradas no usuário que se alinham com a identidade da sua marca.',
    'process.step.3.title': 'Desenvolvimento',
    'process.step.3.desc': 'Codificamos com precisão usando frameworks modernos como Next.js para velocidade, segurança e escalabilidade.',
    'process.step.4.title': 'Lançamento e Crescimento',
    'process.step.4.desc': 'Após um lançamento impecável, otimizamos continuamente com SEO e Ads para impulsionar tráfego e conversões.',

    // CTA
    'cta.title': 'Pronto para dominar seu mercado?',
    'cta.description': 'Vamos discutir como podemos elevar sua presença digital e impulsionar o crescimento real dos negócios.',
    'cta.form.name': 'Nome',
    'cta.form.company': 'Empresa',
    'cta.form.email': 'E-mail',
    'cta.form.message': 'Mensagem',
    'cta.form.send': 'Enviar Mensagem',

    // Footer
    'footer.desc': 'Ajudamos marcas ambiciosas a escalar através de desenvolvimento web de alta performance e marketing digital baseado em dados.',
    'footer.services': 'Serviços',
    'footer.company': 'Empresa',
    'footer.rights': '© 2024 Webzuk. Todos os direitos reservados.',
    'footer.privacy': 'Política de Privacidade',
    'footer.terms': 'Termos de Serviço',
    'footer.links.web': 'Desenvolvimento Web',
    'footer.links.seo': 'Otimização de SEO',
    'footer.links.ads': 'Google Ads',
    'footer.links.ui': 'Design UI/UX',
    'footer.links.about': 'Sobre Nós',
    'footer.links.case': 'Estudos de Caso',
    'footer.links.careers': 'Carreiras',
    'footer.links.contact': 'Contato',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('pt');

  const t = (key: string) => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
